import { useEffect, useMemo, useState } from "react";
import type { PvModuleSpec } from "@pv/engine";
import {
  api, downloadFile, geocode,
  type CatalogueBattery, type CatalogueInverter, type CatalogueModule,
  type AutoDesignResponse, type CatalogueEms, type CatalogueEvCharger, type CatalogueHeatPump, type CatalogueMeter,
  type CatalogueMicroinverter, type CatalogueOptimizer, type GeoProviderEval, type MlpeAdvice, type PaidDataRecommendation,
  type DesignCandidate, type InstallationView, type ProcurementOrder, type Supplier,
  type InstallationPackage, type ProjectDetail, type SimulationResponse, type SurfaceDto,
} from "./api.js";
import SiteCanvas, { fitViewFor, type CanvasGeometry, type Tool, type View } from "./SiteCanvas.js";
import { BarChart, CashflowChart } from "./charts.js";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const STATUSES = ["FEASIBILITY", "DESIGN", "TECHNICAL_REVIEW", "PROPOSAL_SENT", "ACCEPTED", "PERMITTING", "PROCUREMENT", "INSTALLATION_PLANNED", "INSTALLING", "COMMISSIONING", "OPERATING", "COMPLETED"];
const STATES = ["Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg", "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen", "Rheinland-Pfalz", "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"];

type StepId = "overview" | "site" | "design" | "package" | "results" | "permits" | "proposals" | "procurement" | "installation";
const STEPS: { id: StepId; icon: string; label: string; sect?: string }[] = [
  { id: "overview", icon: "📋", label: "Overview", sect: "Plan" },
  { id: "site", icon: "🗺️", label: "Site & Roof" },
  { id: "design", icon: "🔌", label: "Design" },
  { id: "package", icon: "🧩", label: "Accessories", sect: "Configure" },
  { id: "results", icon: "⚡", label: "Simulation" },
  { id: "permits", icon: "📑", label: "Regulation (DE)", sect: "Deliver" },
  { id: "proposals", icon: "📄", label: "Proposals" },
  { id: "procurement", icon: "🛒", label: "Procurement", sect: "Execute" },
  { id: "installation", icon: "🔧", label: "Installation" },
];

const fmt = (n: number, d = 0) => n.toLocaleString("de-DE", { maximumFractionDigits: d, minimumFractionDigits: d });
const eur = (n: number) => `${fmt(Math.round(n))} €`;

function toModuleSpec(m: CatalogueModule): PvModuleSpec {
  return {
    id: m.id, manufacturer: m.manufacturer, model: m.model, wp: m.wp,
    widthM: m.widthM, heightM: m.heightM, efficiencyPct: m.efficiencyPct,
    cellTechnology: m.cellTechnology, bifacial: m.bifacial,
    gammaPmaxPctPerK: m.gammaPmaxPctPerK, vocV: m.vocV, betaVocPctPerK: m.betaVocPctPerK,
    vmppV: m.vmppV, imppA: m.imppA, iscA: m.iscA,
    degradationPctPerYear: m.degradationPctPerYear, firstYearDegradationPct: m.firstYearDegradationPct,
    warrantyProductYears: m.warrantyProductYears, warrantyPerformanceYears: m.warrantyPerformanceYears,
    priceEur: m.listPriceEur,
  };
}

export default function ProjectWorkspace({ projectId, onBack }: { projectId: string; onBack: () => void }): JSX.Element {
  const [step, setStep] = useState<StepId>("overview");
  const [detail, setDetail] = useState<ProjectDetail | null>(null);
  const [catalogue, setCatalogue] = useState<{ modules: CatalogueModule[]; inverters: CatalogueInverter[]; batteries: CatalogueBattery[]; evChargers: CatalogueEvCharger[]; heatPumps: CatalogueHeatPump[]; ems: CatalogueEms[]; meters: CatalogueMeter[]; microinverters: CatalogueMicroinverter[]; optimizers: CatalogueOptimizer[] } | null>(null);
  const [sim, setSim] = useState<SimulationResponse | null>(null);
  const [error, setError] = useState("");

  async function reload(): Promise<void> {
    try {
      setDetail(await api.projects.detail(projectId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void reload();
    api.components().then(setCatalogue).catch((e) => setError(String(e)));
  }, [projectId]);

  if (!detail || !catalogue) {
    return <div style={{ padding: 40 }}>{error ? <div className="msg err">{error}</div> : <span className="spinner" />}</div>;
  }

  const r = sim?.result;
  const done: Record<StepId, boolean> = {
    overview: true,
    site: !!detail.site && detail.surfaces.length > 0,
    design: detail.designs.length > 0,
    package: detail.designs.length > 0,
    results: !!sim,
    permits: detail.permitTasks.length > 0,
    procurement: false,
    installation: false,
    proposals: detail.proposals.length > 0,
  };

  return (
    <>
      <div className="proj">
        <nav className="sidenav">
          {STEPS.map((s) => (
            <div key={s.id}>
              {s.sect && <div className="sect">{s.sect}</div>}
              <div className={`step ${step === s.id ? "active" : ""} ${done[s.id] && step !== s.id ? "done" : ""}`} onClick={() => setStep(s.id)}>
                <span className="ic">{s.icon}</span>
                {s.label}
              </div>
            </div>
          ))}
          <div style={{ padding: "18px 16px" }}>
            <button className="btn btn-sm" onClick={onBack}>← All projects</button>
          </div>
        </nav>
        <main className="stage">
          {error && <div className="msg err">{error}</div>}
          {step === "overview" && <Overview detail={detail} sim={sim} onReload={reload} onGoto={setStep} />}
          {step === "site" && <SitePanel detail={detail} onReload={reload} onNext={() => setStep("design")} />}
          {step === "design" && <DesignPanel detail={detail} catalogue={catalogue} onReload={reload} onNext={() => setStep("package")} />}
          {step === "package" && <PackagePanel detail={detail} onReload={reload} onNext={() => setStep("results")} />}
          {step === "results" && <ResultsPanel detail={detail} sim={sim} setSim={setSim} setError={setError} onNext={() => setStep("permits")} />}
          {step === "permits" && <PermitsPanel detail={detail} onReload={reload} onNext={() => setStep("proposals")} />}
          {step === "proposals" && <ProposalsPanel detail={detail} sim={sim} onReload={reload} />}
          {step === "procurement" && <ProcurementPanel detail={detail} onNext={() => setStep("installation")} />}
          {step === "installation" && <InstallationPanel detail={detail} />}
        </main>
      </div>
      <footer className="rollup">
        <div className="ri"><b>{r ? fmt(r.layout.dcKwp, 1) : "–"}</b><span>kWp DC</span></div>
        <div className="ri"><b>{r ? r.layout.moduleCount : "–"}</b><span>Modules</span></div>
        <div className="ri"><b>{r ? fmt(r.energy.annualAcKwh) : "–"}</b><span>kWh / year</span></div>
        <div className="ri"><b>{r ? eur(r.finance.investmentEur) : "–"}</b><span>Investment</span></div>
        <div className="ri"><b>{r?.finance.paybackYears ? `${fmt(r.finance.paybackYears, 1)} yr` : "–"}</b><span>Payback</span></div>
        <div className="ri"><b>{r ? fmt(r.co2SavedTonsPerYear, 1) : "–"}</b><span>t CO₂ / yr</span></div>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 11, color: "var(--muted-2)" }}>STATUS</label>
        <select value={detail.project.status} onChange={(e) => void api.projects.update(projectId, { status: e.target.value }).then(reload)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
        </select>
      </footer>
    </>
  );
}

/* ------------------------------- Overview -------------------------------- */

function Overview({ detail, sim, onReload, onGoto }: {
  detail: ProjectDetail; sim: SimulationResponse | null; onReload: () => Promise<void>; onGoto: (s: StepId) => void;
}): JSX.Element {
  const p = detail.project;
  const r = sim?.result;
  return (
    <div>
      <h1>{p.name}</h1>
      <div className="sub">{detail.site ? `${detail.site.addressLine ?? ""}, ${detail.site.city ?? ""} · ${detail.site.state ?? ""}` : "No site defined yet"} · {p.projectType.replaceAll("_", " ").toLowerCase()}</div>
      <div className="tiles">
        <div className="tile hl"><div className="v">{r ? `${fmt(r.layout.dcKwp, 1)} kWp` : "—"}</div><div className="l">System size {r ? `(${r.layout.moduleCount} modules)` : "(simulate to compute)"}</div></div>
        <div className="tile"><div className="v">{r ? `${fmt(r.energy.annualAcKwh)} kWh` : "—"}</div><div className="l">Est. annual production</div></div>
        <div className="tile"><div className="v">{r ? eur(r.finance.investmentEur) : "—"}</div><div className="l">Investment (net)</div></div>
        <div className="tile"><div className="v">{r?.finance.paybackYears ? `${fmt(r.finance.paybackYears, 1)} yr` : "—"}</div><div className="l">Payback</div></div>
      </div>
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginBottom: 10 }}>Project progress</h3>
        {[
          ["Site & roof drawn", detail.surfaces.length > 0, "site"],
          ["Design configured", detail.designs.length > 0, "design"],
          ["Simulation run", !!sim, "results"],
          ["Regulation checklist", detail.permitTasks.length > 0, "permits"],
          ["Proposal created", detail.proposals.length > 0, "proposals"],
        ].map(([label, ok, target], i) => (
          <div key={i} className={`check ${ok ? "donec" : ""}`} style={{ cursor: "pointer" }} onClick={() => onGoto(target as StepId)}>
            <div className="cb">{ok ? "✓" : ""}</div>
            <div className="t"><b>{label as string}</b><span>{ok ? "Done" : "Open — click to continue"}</span></div>
          </div>
        ))}
      </div>
      <button className="btn btn-primary" onClick={() => onGoto("site")}>Continue: Site & Roof →</button>
    </div>
  );
}

/* ------------------------------ Site panel ------------------------------- */

interface EditableSurface {
  id: string; // srf-<n> for unsaved
  persistedId: string | null;
  name: string;
  kind: string;
  geometry: CanvasGeometry;
  azimuthDeg: number;
  tiltDeg: number;
  edgeSetbackM: number;
  gridRotationDeg: number;
}

function toEditable(s: SurfaceDto): EditableSurface {
  return {
    id: s.id,
    persistedId: s.id,
    name: s.name,
    kind: s.kind,
    geometry: { vertices: s.boundaryLocal.vertices, obstacles: s.obstacles },
    azimuthDeg: s.azimuthDeg,
    tiltDeg: s.tiltDeg,
    edgeSetbackM: s.edgeSetbackM,
    gridRotationDeg: s.gridRotationDeg ?? 0,
  };
}

function GeoSourcesCard({ detail, onReload }: { detail: ProjectDetail; onReload: () => Promise<void> }): JSX.Element {
  const [evals, setEvals] = useState<GeoProviderEval[] | null>(null);
  const [decision, setDecision] = useState("");
  const [busy, setBusy] = useState(false);
  const [paidRec, setPaidRec] = useState<PaidDataRecommendation | null>(null);
  const [approval, setApproval] = useState("");

  useEffect(() => {
    api.projects.dataRecommendation(detail.project.id).then((r) => setPaidRec(r.recommendation)).catch(() => undefined);
  }, [detail.project.id]);

  async function load(): Promise<void> {
    setBusy(true);
    try {
      const r = await api.projects.twinProviders(detail.project.id);
      setEvals(r.evaluated);
      setDecision(r.decision);
    } finally {
      setBusy(false);
    }
  }

  async function runPipeline(): Promise<void> {
    setBusy(true);
    try {
      const r = await api.projects.twinReconstruct(detail.project.id, { apply: true });
      setEvals(r.evaluated);
      setDecision(r.decision);
      if (r.surfacesCreated.length > 0) await onReload();
    } finally {
      setBusy(false);
    }
  }

  const badge = (e: GeoProviderEval): string =>
    e.available ? "chip ok" : e.status === "commercial_stub" ? "chip" : "chip info";
  const costLabel = (e: GeoProviderEval): string =>
    e.cost.model === "free_open_data" ? "Open Data · 0 €"
    : e.cost.model === "included_project_upload" ? "own data · 0 €"
    : e.cost.model === "free_api_quota" ? "free quota"
    : e.cost.model === "per_report" ? `~${e.cost.estimatedEurPerSite ?? "?"} €/report`
    : e.cost.model === "subscription" ? "subscription"
    : "quote";

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <h3 style={{ marginBottom: 4 }}>🛰️ Geometry sources (Digital Twin)</h3>
      <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Hybrid pipeline: best available data per project — uploads → open government LiDAR → Google Solar → OSM+AI → manual. Paid sources are shown with cost, never purchased automatically.
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn btn-o btn-sm" disabled={busy} onClick={() => void load()}>{busy ? <span className="spinner" /> : "Evaluate sources"}</button>
        <button className="btn btn-sm" disabled={busy} onClick={() => void runPipeline()}>▶ Reconstruct & apply best source</button>
      </div>
      {decision && <div className="msg info" style={{ marginTop: 8 }}>{decision}</div>}
      {paidRec && (
        <div className="card" style={{ marginTop: 10, borderLeft: "3px solid var(--warn)" }}>
          <b>Higher accuracy available: {paidRec.providerName}</b>
          <div className="muted" style={{ fontSize: 12, margin: "6px 0" }}>{paidRec.why}</div>
          <div style={{ fontSize: 12.5 }}>
            Estimated cost: <b>{paidRec.estimatedCostEur != null ? `${paidRec.estimatedCostEur} €` : paidRec.costModel}</b> ·
            Expected benefit: confidence <b>{paidRec.currentConfidencePct} % → {paidRec.expectedConfidencePct} %</b> ·
            {paidRec.necessity === "optional" ? " Optional — use for final engineering." : " Recommended for permit-grade design."}
          </div>
          <button
            className="btn btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => {
              void api.projects.approvePaidData(detail.project.id, paidRec.providerId).then((r) => setApproval(r.note));
            }}
          >
            Approve purchase (admin)
          </button>
          {approval && <div className="msg info" style={{ marginTop: 6 }}>{approval}</div>}
          <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>Requires explicit approval — never purchased automatically.</div>
        </div>
      )}
      {evals && (
        <table className="tbl" style={{ marginTop: 8 }}>
          <thead><tr><th>Source</th><th>Status</th><th className="r">Cost</th></tr></thead>
          <tbody>
            {evals.map((e) => (
              <tr key={e.providerId}>
                <td><b>{e.name}</b><br /><span className="muted" style={{ fontSize: 10.5 }}>{e.coverageNote} · {e.cost.licensingNote}</span></td>
                <td><span className={badge(e)}>{e.available ? "available" : e.status === "commercial_stub" ? "needs contract" : e.status === "coverage_directory" ? "coming" : "no data"}</span></td>
                <td className="r" style={{ whiteSpace: "nowrap" }}>{costLabel(e)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SitePanel({ detail, onReload, onNext }: { detail: ProjectDetail; onReload: () => Promise<void>; onNext: () => void }): JSX.Element {
  const pid = detail.project.id;
  const defaultKind = detail.project.projectType.includes("FLAT")
    ? "flat_roof"
    : detail.project.projectType === "GROUND_MOUNTED"
      ? "ground"
      : "pitched_roof";

  const [address, setAddress] = useState(detail.site?.addressLine ?? "");
  const [state, setState] = useState(detail.site?.state ?? "Bayern");
  const [lat, setLat] = useState(detail.site?.lat ?? 48.137);
  const [lon, setLon] = useState(detail.site?.lon ?? 11.575);
  const [cons, setCons] = useState(detail.site?.annualConsumptionKwh ?? 4500);
  const [price, setPrice] = useState(detail.site?.electricityPriceCtKwh ?? 37);
  const [profile, setProfile] = useState(detail.site?.loadProfile ?? "H0");

  const [surfaces, setSurfaces] = useState<EditableSurface[]>(() =>
    detail.surfaces.length > 0
      ? detail.surfaces.map(toEditable)
      : [{ id: "srf-1", persistedId: null, name: "Roof 1", kind: defaultKind, geometry: { vertices: [], obstacles: [] }, azimuthDeg: 180, tiltDeg: 35, edgeSetbackM: 0.3, gridRotationDeg: 0 }],
  );
  const [deleted, setDeleted] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string>(surfaces[0]?.id ?? "srf-1");
  const [view, setView] = useState<View>(fitViewFor(surfaces));
  const [tool, setTool] = useState<Tool>("draw");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const active = surfaces.find((s) => s.id === activeId) ?? surfaces[0]!;

  function patchActive(patch: Partial<EditableSurface>): void {
    setSurfaces((all) => all.map((s) => (s.id === active.id ? { ...s, ...patch } : s)));
  }

  function addSurface(): void {
    const n = surfaces.length + 1;
    const id = `srf-new-${n}-${Date.now() % 10000}`;
    setSurfaces((all) => [...all, { id, persistedId: null, name: `Roof ${n}`, kind: defaultKind, geometry: { vertices: [], obstacles: [] }, azimuthDeg: 180, tiltDeg: 35, edgeSetbackM: 0.3, gridRotationDeg: 0 }]);
    setActiveId(id);
    setTool("draw");
  }

  function removeSurface(id: string): void {
    const s = surfaces.find((x) => x.id === id);
    if (!s) return;
    if (s.persistedId) setDeleted((d) => [...d, s.persistedId!]);
    const rest = surfaces.filter((x) => x.id !== id);
    setSurfaces(rest);
    if (activeId === id && rest[0]) setActiveId(rest[0].id);
  }

  async function detect(provider: "osm" | "google_solar"): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      // Persist the site first so detection uses the current pin
      await api.projects.putSite(pid, {
        addressLine: address, state, lat, lon,
        annualConsumptionKwh: cons, loadProfile: profile, electricityPriceCtKwh: price,
      });
      const res = await api.projects.detectSurfaces(pid, provider);
      const imported = res.surfaces.map(toEditable);
      setSurfaces((all) => {
        // drop empty unsaved placeholders, keep drawn ones
        const kept = all.filter((x) => x.persistedId || x.geometry.vertices.length >= 3);
        return [...kept, ...imported];
      });
      if (imported[0]) setActiveId(imported[0].id);
      setView(fitViewFor([...surfaces, ...imported]));
      setNote(`✓ ${res.surfaces.length} surface(s) detected via ${provider === "osm" ? "OpenStreetMap footprint" : "Google Solar AI"} — adjust corners on the imagery, then save.`);
      await onReload();
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function findAddress(): Promise<void> {
    if (!address.trim()) return;
    const g = await geocode(address);
    if (g) {
      setLat(g.lat);
      setLon(g.lon);
      setNote(`📍 ${g.label}`);
    } else {
      setNote("Address not found — check spelling or set coordinates manually.");
    }
  }

  async function save(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      await api.projects.putSite(pid, {
        addressLine: address, state, lat, lon,
        annualConsumptionKwh: cons, loadProfile: profile, electricityPriceCtKwh: price,
      });
      for (const delId of deleted) await api.projects.deleteSurface(pid, delId);
      for (const s of surfaces) {
        if (s.geometry.vertices.length < 3) continue;
        const payload = {
          name: s.name,
          kind: s.kind,
          boundaryLocal: { vertices: s.geometry.vertices },
          obstacles: s.geometry.obstacles,
          azimuthDeg: s.azimuthDeg,
          tiltDeg: s.tiltDeg,
          edgeSetbackM: s.edgeSetbackM,
          obstacleBufferM: 0.25,
          gridRotationDeg: s.gridRotationDeg,
        };
        if (s.persistedId) await api.projects.updateSurface(pid, s.persistedId, payload);
        else await api.projects.addSurface(pid, payload as Omit<SurfaceDto, "id">);
      }
      setDeleted([]);
      await onReload();
      setNote("✓ Site & surfaces saved.");
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>Site & Roof surfaces</h1>
      <div className="sub">Locate the building, then draw each usable roof/land surface. Rotate the module grid until rows align with the ridge on the photo. Drag = pan, scroll = zoom, right-click removes obstacles.</div>
      <div className="frow" style={{ marginBottom: 12 }}>
        <div className="field" style={{ flex: 2 }}>
          <label>Address</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input style={{ flex: 1 }} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street number, city" onKeyDown={(e) => e.key === "Enter" && void findAddress()} />
            <button className="btn btn-o" onClick={() => void findAddress()}>📍 Find</button>
          </div>
        </div>
        <div className="field"><label>Bundesland</label>
          <select value={state} onChange={(e) => setState(e.target.value)}>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
        </div>
        <div className="field"><label>Consumption (kWh/yr)</label><input type="number" value={cons} onChange={(e) => setCons(+e.target.value)} /></div>
        <div className="field"><label>El. price (ct/kWh)</label><input type="number" step={0.5} value={price} onChange={(e) => setPrice(+e.target.value)} /></div>
        <div className="field"><label>Load profile</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value)}>
            <option value="H0">H0 — Household</option>
            <option value="G0">G0 — Commerce (general)</option>
            <option value="G1">G1 — Business hours Mo–Fr</option>
            <option value="L0">L0 — Agriculture</option>
          </select>
        </div>
      </div>

      {/* Surface manager */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        {surfaces.map((s) => (
          <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <button className={`btn btn-sm ${s.id === activeId ? "active" : ""}`} onClick={() => setActiveId(s.id)}>
              {s.name} {s.geometry.vertices.length > 2 ? "✓" : "✏️"}
            </button>
            {surfaces.length > 1 && (
              <button className="btn btn-sm" title="Delete surface" onClick={() => removeSurface(s.id)}>✕</button>
            )}
          </span>
        ))}
        <button className="btn btn-sm btn-o" onClick={addSurface}>＋ Add surface</button>
        <button className="btn btn-sm btn-o" disabled={busy} onClick={() => void detect("osm")} title="Import the real building outline from OpenStreetMap (keyless)">🏠 Detect building</button>
        <button className="btn btn-sm btn-o" disabled={busy} onClick={() => void detect("google_solar")} title="AI roof segments with pitch & azimuth (Google Solar API, key required)">✨ AI roof segments</button>
        <input style={{ width: 140 }} value={active.name} onChange={(e) => patchActive({ name: e.target.value })} title="Surface name" />
        <select value={active.kind} onChange={(e) => patchActive({ kind: e.target.value })}>
          <option value="pitched_roof">Pitched roof</option>
          <option value="flat_roof">Flat roof</option>
          <option value="ground">Ground</option>
          <option value="carport">Carport</option>
          <option value="facade">Facade</option>
        </select>
      </div>

      <div className="canvas-wrap">
        <div className="canvas-tools">
          <button className={`btn btn-sm ${tool === "draw" ? "active" : ""}`} onClick={() => setTool("draw")}>✏️ Draw boundary</button>
          <button className={`btn btn-sm ${tool === "obstacle" ? "active" : ""}`} onClick={() => setTool("obstacle")}>⛔ Obstacle</button>
          <span className="sep" />
          <div className="slider-field">Azimuth <input type="range" min={90} max={270} step={5} value={active.azimuthDeg} onChange={(e) => patchActive({ azimuthDeg: +e.target.value })} /><b>{active.azimuthDeg}°</b></div>
          <div className="slider-field">Tilt <input type="range" min={0} max={60} value={active.tiltDeg} onChange={(e) => patchActive({ tiltDeg: +e.target.value })} /><b>{active.tiltDeg}°</b></div>
          <div className="slider-field">↻ Grid <input type="range" min={-90} max={90} step={1} value={active.gridRotationDeg} onChange={(e) => patchActive({ gridRotationDeg: +e.target.value })} /><b>{active.gridRotationDeg}°</b></div>
          <div className="slider-field">Setback <input type="range" min={0} max={2} step={0.1} value={active.edgeSetbackM} onChange={(e) => patchActive({ edgeSetbackM: +e.target.value })} /><b>{active.edgeSetbackM} m</b></div>
          <span className="sep" />
          <button className="btn btn-sm" onClick={() => setView((v) => ({ ...v, S: Math.min(45, v.S * 1.3) }))}>＋</button>
          <button className="btn btn-sm" onClick={() => setView((v) => ({ ...v, S: Math.max(1.5, v.S * 0.77) }))}>−</button>
          <button className="btn btn-sm" onClick={() => setView(fitViewFor(surfaces))}>⤢ Fit</button>
          <span className="sep" />
          <button className="btn btn-sm" onClick={() => patchActive({ geometry: { vertices: [], obstacles: [] } })}>Clear surface</button>
        </div>
        <SiteCanvas
          lat={lat} lon={lon}
          surfaces={surfaces.map((s) => ({ id: s.id, geometry: s.geometry, rotationDeg: s.gridRotationDeg, edgeSetbackM: s.edgeSetbackM, kind: s.kind }))}
          activeSurfaceId={active.id}
          view={view} tool={tool} mode="site"
          onGeometryChange={(sid, g) => setSurfaces((all) => all.map((s) => (s.id === sid ? { ...s, geometry: g } : s)))}
          onViewChange={setView}
        />
        <div className="legend">
          <span>{surfaces.length} surface(s) · active: {active.name} ({active.geometry.vertices.length} pts, {active.geometry.obstacles.length} obstacles)</span>
          <span>Imagery: Esri World Imagery (live)</span>
        </div>
      </div>
      {note && <div className={`msg ${note.startsWith("Error") ? "err" : "info"}`}>{note}</div>}
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? <span className="spinner" /> : "Save site & surfaces"}</button>
        <button className="btn" onClick={onNext}>Continue: Design →</button>
      </div>
      <GeoSourcesCard detail={detail} onReload={onReload} />
      <div>
      </div>
    </div>
  );
}

/* ------------------------------ Design panel ----------------------------- */

type Overrides = Record<string, { removed: Set<string>; moved: Record<string, { dx: number; dy: number }> }>;

function DesignPanel({ detail, catalogue, onReload, onNext }: {
  detail: ProjectDetail;
  catalogue: { modules: CatalogueModule[]; inverters: CatalogueInverter[]; batteries: CatalogueBattery[]; evChargers: CatalogueEvCharger[]; heatPumps: CatalogueHeatPump[]; ems: CatalogueEms[]; meters: CatalogueMeter[]; microinverters: CatalogueMicroinverter[]; optimizers: CatalogueOptimizer[] };
  onReload: () => Promise<void>;
  onNext: () => void;
}): JSX.Element {
  const pid = detail.project.id;
  const last = detail.designs[detail.designs.length - 1];
  const [moduleId, setModuleId] = useState(last?.moduleId ?? "mod-jinko-450");
  const [moduleWp, setModuleWp] = useState<number | undefined>(last?.moduleWp ?? undefined);
  const [inverterId, setInverterId] = useState(last?.inverterId ?? "inv-fronius-10");
  const [useIntegrated, setUseIntegrated] = useState(last ? last.inverterId === null : false);
  const [inverterKw, setInverterKw] = useState<number | undefined>(last?.inverterAcKw ?? undefined);
  const [batteryId, setBatteryId] = useState<string | null>(last?.batteryId ?? null);
  const [microinverterId, setMicroinverterId] = useState<string | null>(last?.microinverterId ?? null);
  const [mlpeAdvice, setMlpeAdvice] = useState<MlpeAdvice | null>(null);
  const [mlpeBusy, setMlpeBusy] = useState(false);
  const [optimizerId, setOptimizerId] = useState<string | null>(last?.optimizerId ?? null);
  const [evChargerId, setEvChargerId] = useState<string | null>(last?.evChargerId ?? null);
  const [heatPumpId, setHeatPumpId] = useState<string | null>(last?.heatPumpId ?? null);
  const [emsId, setEmsId] = useState<string | null>(last?.emsId ?? null);
  const [meterId, setMeterId] = useState<string | null>(last?.meterId ?? null);
  const [dispatchStrategy, setDispatchStrategy] = useState<"self_consumption" | "price_optimized">(
    (last?.dispatchStrategy as "self_consumption" | "price_optimized") ?? "self_consumption",
  );
  const [orientation, setOrientation] = useState<"portrait" | "landscape">(last?.layoutOptions.orientation ?? "portrait");
  const [rowGap, setRowGap] = useState(last?.layoutOptions.rowGapM ?? 0);
  const [overrides, setOverrides] = useState<Overrides>(() => {
    const out: Overrides = {};
    const so = last?.surfaceOverrides ?? {};
    for (const [sid, ov] of Object.entries(so)) {
      out[sid] = { removed: new Set(ov.removedModuleKeys ?? []), moved: { ...(ov.movedModules ?? {}) } };
    }
    return out;
  });
  const [activeSid, setActiveSid] = useState<string | null>(detail.surfaces[0]?.id ?? null);
  const [stats, setStats] = useState<Record<string, { count: number; kwp: number }>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [view, setView] = useState<View>(fitViewFor(detail.surfaces.map((s) => ({ geometry: { vertices: s.boundaryLocal.vertices, obstacles: [] } }))));

  const selectedModule = catalogue.modules.find((x) => x.id === moduleId);
  const moduleSpec = useMemo(() => {
    if (!selectedModule) return null;
    const spec = toModuleSpec(selectedModule);
    const v = moduleWp !== undefined ? selectedModule.variants?.find((x) => x.wp === moduleWp) : undefined;
    if (v) {
      spec.wp = v.wp;
      spec.vocV = v.vocV;
      spec.vmppV = v.vmppV;
      spec.imppA = v.imppA;
      spec.iscA = v.iscA;
      spec.efficiencyPct = v.efficiencyPct;
      spec.priceEur = v.priceEur;
    }
    return spec;
  }, [selectedModule, moduleWp]);

  const inverter = catalogue.inverters.find((i) => i.id === inverterId);
  const inverterVariant = inverterKw !== undefined ? inverter?.variants?.find((v) => v.acKw === inverterKw) : undefined;
  const battery = batteryId ? catalogue.batteries.find((b) => b.id === batteryId) : null;
  const integratedAvailable = !!battery?.integratedInverter;
  const integrated = useIntegrated && integratedAvailable;
  const effectiveAcKw = integrated
    ? battery!.integratedInverter!.acKw
    : (inverterVariant?.acKw ?? inverter?.acKw ?? 10);
  const compatible =
    integrated || !battery || battery.acCoupled || (inverter?.compatibleBatteryIds.includes(battery.id) ?? false);

  const totals = Object.values(stats).reduce((a, s) => ({ count: a.count + s.count, kwp: a.kwp + s.kwp }), { count: 0, kwp: 0 });
  const suggestedInverterCount = integrated ? 1 : Math.max(1, Math.ceil((totals.kwp * 0.85) / effectiveAcKw));

  const canvasSurfaces = detail.surfaces.map((s) => ({
    id: s.id,
    geometry: { vertices: s.boundaryLocal.vertices, obstacles: s.obstacles },
    rotationDeg: s.gridRotationDeg ?? 0,
    edgeSetbackM: s.edgeSetbackM,
    kind: s.kind,
    removedKeys: overrides[s.id]?.removed,
    movedOffsets: overrides[s.id]?.moved,
  }));

  function ov(sid: string) {
    return overrides[sid] ?? { removed: new Set<string>(), moved: {} };
  }

  async function saveDesign(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      const surfaceOverrides: Record<string, { removedModuleKeys: string[]; movedModules: Record<string, { dx: number; dy: number }> }> = {};
      for (const [sid, o] of Object.entries(overrides)) {
        surfaceOverrides[sid] = { removedModuleKeys: [...o.removed], movedModules: o.moved };
      }
      await api.projects.addDesign(pid, {
        name: `Variant ${String.fromCharCode(65 + detail.designs.length)}`,
        moduleId,
        inverterId: integrated || microinverterId ? null : inverterId,
        inverterCount: suggestedInverterCount,
        batteryId,
        microinverterId,
        optimizerId: microinverterId ? null : optimizerId,
        evChargerId,
        heatPumpId,
        emsId,
        meterId,
        dispatchStrategy,
        moduleWp,
        inverterAcKw: integrated ? undefined : inverterKw,
        surfaceOverrides,
        layoutOptions: { orientation, moduleGapM: 0.02, rowGapM: rowGap },
        commissioningDate: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
      });
      await onReload();
      setNote("✓ Design saved as new version.");
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  const movedCount = Object.values(overrides).reduce((n, o) => n + Object.keys(o.moved).length, 0);
  const removedCount = Object.values(overrides).reduce((n, o) => n + o.removed.size, 0);

  return (
    <div>
      <h1>Design — components & layout</h1>
      <div className="sub">All roof surfaces feed one inverter. Click a module to remove it; <b>drag a module to move it</b> (red = invalid position). The grid follows each surface's rotation from the Site step.</div>
      {detail.surfaces.length === 0 && <div className="msg err">No surfaces drawn yet — go back to Site & Roof first.</div>}
      {detail.surfaces.length > 0 && moduleSpec && (
        <div className="canvas-wrap">
          <div className="canvas-tools">
            {detail.surfaces.length > 1 && (
              <>
                <label style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>EDIT</label>
                <select value={activeSid ?? ""} onChange={(e) => setActiveSid(e.target.value)}>
                  {detail.surfaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <span className="sep" />
              </>
            )}
            <button className={`btn btn-sm ${orientation === "portrait" ? "active" : ""}`} onClick={() => { setOrientation("portrait"); setOverrides({}); }}>▯ Portrait</button>
            <button className={`btn btn-sm ${orientation === "landscape" ? "active" : ""}`} onClick={() => { setOrientation("landscape"); setOverrides({}); }}>▭ Landscape</button>
            {detail.project.projectType !== "RESIDENTIAL_PITCHED" && (
              <div className="slider-field">Row gap <input type="range" min={0} max={6} step={0.25} value={rowGap} onChange={(e) => { setRowGap(+e.target.value); setOverrides({}); }} /><b>{rowGap} m</b></div>
            )}
            {(removedCount > 0 || movedCount > 0) && (
              <button className="btn btn-sm" onClick={() => setOverrides({})}>↺ Reset ({removedCount} removed, {movedCount} moved)</button>
            )}
            <span className="sep" />
            <span style={{ fontSize: 12.5 }}>
              <b>{totals.count}</b> modules · <b>{fmt(totals.kwp, 2)} kWp</b>
              {moduleSpec && <> · {fmt(totals.count * moduleSpec.widthM * moduleSpec.heightM, 1)} m² · {moduleSpec.widthM.toFixed(3)} × {moduleSpec.heightM.toFixed(3)} m</>}
            </span>
            <span className="sep" />
            <button className="btn btn-sm" onClick={() => setView((v) => ({ ...v, S: Math.min(45, v.S * 1.3) }))}>＋</button>
            <button className="btn btn-sm" onClick={() => setView((v) => ({ ...v, S: Math.max(1.5, v.S * 0.77) }))}>−</button>
          </div>
          <SiteCanvas
            lat={detail.site?.lat ?? 48.14} lon={detail.site?.lon ?? 11.58}
            surfaces={canvasSurfaces}
            activeSurfaceId={activeSid}
            view={view} tool="module" mode="layout"
            module={moduleSpec}
            layoutOptions={{ orientation, moduleGapM: 0.02, rowGapM: rowGap }}
            onViewChange={setView}
            onToggleModule={(sid, key) =>
              setOverrides((all) => {
                const o = ov(sid);
                const removed = new Set(o.removed);
                if (removed.has(key)) removed.delete(key);
                else removed.add(key);
                return { ...all, [sid]: { ...o, removed } };
              })
            }
            onMoveModule={(sid, key, dx, dy) =>
              setOverrides((all) => {
                const o = ov(sid);
                const moved = { ...o.moved };
                if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) delete moved[key];
                else moved[key] = { dx, dy };
                return { ...all, [sid]: { ...o, moved } };
              })
            }
            onModuleStats={(perSurface) => {
              const changed = JSON.stringify(perSurface) !== JSON.stringify(stats);
              if (changed) setStats(perSurface);
            }}
          />
          <div className="legend">
            {detail.surfaces.map((s) => (
              <span key={s.id}>{s.name}: <b>{stats[s.id]?.count ?? 0}</b> mod · {fmt(stats[s.id]?.kwp ?? 0, 1)} kWp{(s.gridRotationDeg ?? 0) !== 0 ? ` · ↻${s.gridRotationDeg}°` : ""}</span>
            ))}
            <span>Click = remove/restore · Drag = move (1 cm snap) · red = invalid position</span>
          </div>
        </div>
      )}
      <div className="comp-cols">
        <div>
          <h3 style={{ marginBottom: 8 }}>PV Module</h3>
          <div className="comp-list">
            {catalogue.modules.map((m) => (
              <div key={m.id} className={`ccard ${m.id === moduleId ? "sel" : ""}`} onClick={() => { if (m.id !== moduleId) { setModuleId(m.id); setModuleWp(undefined); } }}>
                <span className="pr">{(m.id === moduleId && moduleSpec ? moduleSpec.priceEur : m.listPriceEur)} €</span>
                <div className="nm">{m.manufacturer} {m.model.split(" ").slice(0, 3).join(" ")}</div>
                <div className="meta">
                  {m.id === moduleId && moduleSpec ? moduleSpec.wp : m.wp} Wp · {(m.id === moduleId && moduleSpec ? moduleSpec.efficiencyPct : m.efficiencyPct).toFixed(1)}% · {m.cellTechnology}
                  <br />{m.widthM.toFixed(3)} × {m.heightM.toFixed(3)} m · {(m.widthM * m.heightM).toFixed(2)} m² · {m.warrantyProductYears}/{m.warrantyPerformanceYears} yr
                </div>
                {m.id === moduleId && m.variants && m.variants.length > 1 && (
                  <div style={{ marginTop: 7 }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginRight: 6 }}>Power class</label>
                    <select value={moduleWp ?? m.wp} onChange={(e) => setModuleWp(+e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                      {m.variants.map((v) => (
                        <option key={v.wp} value={v.wp}>{v.wp} Wp — {v.priceEur} € (Voc {v.vocV.toFixed(1)} V)</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 style={{ marginBottom: 8 }}>Inverter</h3>
          <div className="comp-list">
            {integratedAvailable && (
              <div className={`ccard ${integrated ? "sel" : ""}`} onClick={() => setUseIntegrated(true)}>
                <span className="pr">0 €</span>
                <div className="nm">⚡ Integrated in {battery!.manufacturer} {battery!.model}</div>
                <div className="meta">{battery!.integratedInverter!.acKw} kW AC hybrid · {battery!.integratedInverter!.mppt.length} MPPT · included in battery price</div>
              </div>
            )}
            {catalogue.inverters.map((i) => (
              <div key={i.id} className={`ccard ${!integrated && i.id === inverterId ? "sel" : ""}`} onClick={() => { setUseIntegrated(false); if (i.id !== inverterId) { setInverterId(i.id); setInverterKw(undefined); } }}>
                <span className="pr">{fmt(i.id === inverterId && inverterVariant ? inverterVariant.priceEur : i.listPriceEur)} €</span>
                <div className="nm">{i.manufacturer} {i.id === inverterId && inverterVariant ? inverterVariant.label : i.model}</div>
                <div className="meta">{i.id === inverterId ? effectiveAcKw : i.acKw} kW AC · {i.hybrid ? "hybrid" : "string"} · {i.class}</div>
                {i.id === inverterId && i.variants && i.variants.length > 1 && (
                  <div style={{ marginTop: 7 }} onClick={(e) => e.stopPropagation()}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.4, marginRight: 6 }}>Power class</label>
                    <select value={inverterKw ?? i.acKw} onChange={(e) => setInverterKw(+e.target.value)} style={{ padding: "3px 6px", fontSize: 12 }}>
                      {i.variants.map((v) => (
                        <option key={v.acKw} value={v.acKw}>{v.acKw} kW — {v.label} ({fmt(v.priceEur)} €)</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>
          {totals.kwp > 0 && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Suggested: {suggestedInverterCount}× for {fmt(totals.kwp, 1)} kWp total (DC/AC {fmt(totals.kwp / (effectiveAcKw * suggestedInverterCount), 2)})</div>}
        </div>
        <div>
          <h3 style={{ marginBottom: 8 }}>Battery & operation</h3>
          <div className="comp-list">
            <div className={`ccard ${batteryId === null ? "sel" : ""}`} onClick={() => { setBatteryId(null); setUseIntegrated(false); }}>
              <div className="nm">No battery</div>
              <div className="meta">Feed all surplus into the grid</div>
            </div>
            {catalogue.batteries.map((b) => (
              <div key={b.id} className={`ccard ${b.id === batteryId ? "sel" : ""}`} onClick={() => { setBatteryId(b.id); if (b.integratedInverter) setUseIntegrated(true); else setUseIntegrated(false); }}>
                <span className="pr">{fmt(b.listPriceEur)} €</span>
                <div className="nm">{b.manufacturer} {b.model}</div>
                <div className="meta">{b.usableKwh} kWh · {b.integratedInverter ? `hybrid incl. ${b.integratedInverter.acKw} kW inverter` : b.acCoupled ? "AC-coupled" : "DC"} · {b.warrantyYears} yr</div>
              </div>
            ))}
          </div>
          <div className="field" style={{ marginTop: 8 }}>
            <label>Battery operation strategy</label>
            <select value={dispatchStrategy} onChange={(e) => setDispatchStrategy(e.target.value as "self_consumption" | "price_optimized")}>
              <option value="self_consumption">Maximize self-consumption (flat tariff)</option>
              <option value="price_optimized">Price-optimized (dynamic tariff, e.g. Tibber/aWATTar)</option>
            </select>
          </div>
        </div>
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <h3 style={{ marginBottom: 4 }}>🔬 Module-level electronics (MLPE)</h3>
        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Microinverters replace the string inverter entirely (AC modules, per-module MPPT, no series strings — needs AC-coupled storage). Optimizers pair with a string inverter and recover series-mismatch losses on shaded roofs. SMA ShadeFix and Fronius Dynamic Peak Manager are inverter firmware — already active with those brands at no extra cost.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="field">
            <label>⚡ Microinverter (replaces string inverter)</label>
            <select
              value={microinverterId ?? ""}
              onChange={(e) => {
                setMicroinverterId(e.target.value || null);
                if (e.target.value) setOptimizerId(null);
              }}
            >
              <option value="">— none (string inverter) —</option>
              {catalogue.microinverters.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.unitAcVa} VA · {fmt(x.listPriceEur)} €/unit</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>🔧 DC optimizer (per module, with string inverter)</label>
            <select value={optimizerId ?? ""} disabled={!!microinverterId} onChange={(e) => setOptimizerId(e.target.value || null)}>
              <option value="">— none —</option>
              {catalogue.optimizers.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model}{x.requiresInverterBrand ? ` · requires ${x.requiresInverterBrand}` : x.compatibleInverterBrands.includes("*") ? " · any inverter" : ` · ${x.compatibleInverterBrands.join("/")}`} · {fmt(x.listPriceEur)} €/module</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            className="btn btn-o btn-sm"
            disabled={mlpeBusy || detail.designs.length === 0}
            onClick={() => {
              const d = detail.designs[detail.designs.length - 1]!;
              setMlpeBusy(true);
              api.projects
                .mlpeAdvice(pid, d.id)
                .then(setMlpeAdvice)
                .catch(() => setMlpeAdvice(null))
                .finally(() => setMlpeBusy(false));
            }}
          >
            {mlpeBusy ? <span className="spinner" /> : "🧠 Should this design use MLPE? (AI Engineer)"}
          </button>
          {detail.designs.length === 0 && <span className="muted" style={{ fontSize: 11.5, marginLeft: 8 }}>save a design first</span>}
        </div>
        {mlpeAdvice && (
          <div style={{ marginTop: 10 }}>
            <div className="msg info">
              Shade dispersion {mlpeAdvice.shadeDispersionPct} % → series mismatch {mlpeAdvice.seriesMismatchLossPct} % on a string architecture. Baseline: {mlpeAdvice.baseline.architecture}, {fmt(mlpeAdvice.baseline.annualAcKwh)} kWh/a, NPV {eur(mlpeAdvice.baseline.npvEur)}.
            </div>
            <table className="tbl" style={{ marginTop: 8 }}>
              <thead><tr><th>Option</th><th className="r">Gain</th><th className="r">Added cost</th><th className="r">ΔNPV</th><th className="r">ΔPayback</th><th>Verdict</th></tr></thead>
              <tbody>
                {mlpeAdvice.options.map((o) => (
                  <tr key={o.componentId}>
                    <td><b>{o.label}</b><br /><span className="muted" style={{ fontSize: 10.5 }}>{o.reasons[0]}</span></td>
                    <td className="r">{o.gainKwhYear} kWh (+{o.gainPct} %)</td>
                    <td className="r">{eur(o.addedInvestmentEur)}</td>
                    <td className="r" style={{ color: o.npvDeltaEur >= 0 ? "var(--pos)" : "var(--neg)" }}>{o.npvDeltaEur >= 0 ? "+" : ""}{eur(o.npvDeltaEur)}</td>
                    <td className="r">{o.paybackDeltaYears != null ? `${o.paybackDeltaYears >= 0 ? "+" : ""}${o.paybackDeltaYears} yr` : "—"}</td>
                    <td>
                      <span className={`chip ${o.verdict === "recommended" || o.verdict === "required" ? "ok" : o.verdict === "incompatible" ? "bad" : ""}`}>
                        {o.verdict === "required" ? "required (architecture)" : o.verdict.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={`msg ${mlpeAdvice.recommendation.choice === "none" ? "info" : "ok"}`} style={{ marginTop: 8 }}>
              {mlpeAdvice.recommendation.explanation}
            </div>
            {mlpeAdvice.recommendation.componentId && (
              <button
                className="btn btn-sm"
                onClick={() => {
                  if (mlpeAdvice.recommendation.choice === "optimizer") {
                    setOptimizerId(mlpeAdvice.recommendation.componentId);
                    setMicroinverterId(null);
                  } else {
                    setMicroinverterId(mlpeAdvice.recommendation.componentId);
                    setOptimizerId(null);
                  }
                }}
              >
                Apply recommendation to design
              </button>
            )}
          </div>
        )}
        {(() => {
          const opt = catalogue.optimizers.find((x) => x.id === optimizerId);
          const invBrand = catalogue.inverters.find((x) => x.id === inverterId)?.manufacturer;
          const bat = catalogue.batteries.find((x) => x.id === batteryId);
          const msgs: { cls: string; text: string }[] = [];
          if (microinverterId && bat && !bat.acCoupled) msgs.push({ cls: "err", text: `${bat.model} is DC-coupled — microinverter systems need AC-coupled storage (e.g. sonnenBatterie).` });
          if (opt?.requiresInverterBrand && invBrand !== opt.requiresInverterBrand && !microinverterId) msgs.push({ cls: "err", text: `${opt.model} only works with ${opt.requiresInverterBrand} inverters (architecture coupling).` });
          if (!microinverterId && !optimizerId && invBrand === "SolarEdge") msgs.push({ cls: "warn", text: "SolarEdge architecture requires optimizers on every module — select S500B/S650B." });
          return msgs.map((m, i) => <div key={i} className={`msg ${m.cls}`} style={{ marginTop: 8 }}>{m.text}</div>);
        })()}
      </div>
      <div className="card" style={{ marginTop: 14 }}>
        <h3 style={{ marginBottom: 4 }}>🏠 Energy ecosystem</h3>
        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Wallbox, heat pump, energy manager and meter are full components: they change the hourly load simulation, autarky and payback, and flow into costing, BOM and purchase orders — exactly like modules, inverters and batteries.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div className="field">
            <label>🚗 EV charger (wallbox)</label>
            <select value={evChargerId ?? ""} onChange={(e) => setEvChargerId(e.target.value || null)}>
              <option value="">— none —</option>
              {catalogue.evChargers.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.acKw} kW{x.pvSurplusCapable ? " · PV-surplus" : ""} · {fmt(x.listPriceEur)} €</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>🔥 Heat pump</label>
            <select value={heatPumpId ?? ""} onChange={(e) => setHeatPumpId(e.target.value || null)}>
              <option value="">— none —</option>
              {catalogue.heatPumps.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.thermalKw} kW th. · SCOP {x.scopW35}{x.sgReady ? " · SG-Ready" : ""} · {fmt(x.listPriceEur)} €</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>🧠 Energy manager (EMS / Smart Home)</label>
            <select value={emsId ?? ""} onChange={(e) => setEmsId(e.target.value || null)}>
              <option value="">— none —</option>
              {catalogue.ems.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model}{x.compatibleInverterBrands.includes("*") ? " · all brands" : ` · ${x.compatibleInverterBrands.join("/")}`}{x.listPriceEur > 0 ? ` · ${fmt(x.listPriceEur)} €` : " · included"}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>⚡ Energy meter (Energiezähler)</label>
            <select value={meterId ?? ""} onChange={(e) => setMeterId(e.target.value || null)}>
              <option value="">— generic (auto in accessories) —</option>
              {catalogue.meters.map((x) => (
                <option key={x.id} value={x.id}>{x.manufacturer} {x.model} · {x.maxCurrentA} A · {x.protocol} · {fmt(x.listPriceEur)} €</option>
              ))}
            </select>
          </div>
        </div>
        {(() => {
          const emsSel = catalogue.ems.find((x) => x.id === emsId);
          const chSel = catalogue.evChargers.find((x) => x.id === evChargerId);
          const hpSel = catalogue.heatPumps.find((x) => x.id === heatPumpId);
          const invBrand = integrated ? "Tesla" : catalogue.inverters.find((x) => x.id === inverterId)?.manufacturer;
          const msgs: { cls: string; text: string }[] = [];
          if (emsSel && !emsSel.compatibleInverterBrands.includes("*") && invBrand && !emsSel.compatibleInverterBrands.includes(invBrand)) {
            msgs.push({ cls: "err", text: `${emsSel.model} only controls ${emsSel.compatibleInverterBrands.join("/")} systems — with ${invBrand} choose an open EMS (Home Assistant, OpenEMS, Loxone).` });
          }
          if (chSel?.pvSurplusCapable && !emsSel) msgs.push({ cls: "info", text: `${chSel.model} supports PV-surplus charging — add an energy manager to activate it (shifts ~50 % of charging into the solar window).` });
          if (hpSel?.sgReady && !emsSel) msgs.push({ cls: "info", text: `${hpSel.model} is SG-Ready — an energy manager enables PV-optimized preheating.` });
          if (emsSel && (chSel?.pvSurplusCapable || hpSel?.sgReady) && (emsSel.compatibleInverterBrands.includes("*") || (invBrand && emsSel.compatibleInverterBrands.includes(invBrand)))) {
            msgs.push({ cls: "ok", text: "Surplus control active: EV charging and/or heat-pump preheating follow PV production in the simulation." });
          }
          return msgs.map((m, i) => <div key={i} className={`msg ${m.cls}`} style={{ marginTop: 8 }}>{m.text}</div>);
        })()}
      </div>
      <div style={{ marginTop: 14 }}>
        {!compatible && battery && inverter && (
          <div className="msg err">{battery.manufacturer} {battery.model} is not released for {inverter.manufacturer} {inverter.model}. Choose a compatible battery or an AC-coupled system (Tesla, sonnen).</div>
        )}
        {compatible && battery && (
          <div className="msg ok">
            {integrated
              ? `${battery.model} contains its own ${battery.integratedInverter!.acKw} kW hybrid inverter — no separate PV inverter needed.`
              : battery.acCoupled
                ? `${battery.model} is AC-coupled — works with any inverter.`
                : `${battery.model} is compatible with ${inverter?.model}.`}
          </div>
        )}
        {note && <div className={`msg ${note.startsWith("Error") ? "err" : "ok"}`}>{note}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-primary" disabled={busy || !compatible || detail.surfaces.length === 0} onClick={() => void saveDesign()}>
            {busy ? <span className="spinner" /> : detail.designs.length ? "Save as new variant" : "Save design"}
          </button>
          <button className="btn" onClick={onNext}>Continue: Simulation →</button>
        </div>
        {detail.designs.length > 0 && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Versions:{" "}
            {detail.designs.map((d) => (
              <span key={d.id} style={{ marginRight: 10 }}>
                v{d.version} {d.name.length > 34 ? `${d.name.slice(0, 34)}…` : d.name}{" "}
                <span className={`chip ${d.origin === "ai" ? "info" : ""}`}>{d.origin === "ai" ? "✨ AI" : "manual"}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      <AutoDesignCard detail={detail} onReload={onReload} />
    </div>
  );
}

/* --------------------------- AI Auto-Designer ----------------------------- */

function AutoDesignCard({ detail, onReload }: { detail: ProjectDetail; onReload: () => Promise<void> }): JSX.Element {
  const [sweep, setSweep] = useState<AutoDesignResponse | null>(null);
  const [objective, setObjective] = useState("npv");
  const [budget, setBudget] = useState("");
  const [maxPayback, setMaxPayback] = useState("");
  const [baseline, setBaseline] = useState<{ name: string; npvEur: number; paybackYears: number | null; investmentEur: number; selfSufficiencyPct: number; dcAcRatio: number; annualAcKwh: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [accepting, setAccepting] = useState<number | null>(null);
  const [note, setNote] = useState("");

  const manual = [...detail.designs].reverse().find((d) => (d.origin ?? "manual") === "manual");

  async function runSweep(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      const [sw, base] = await Promise.all([
        api.projects.autodesign(detail.project.id, {
          apply: false,
          objective,
          maxBudgetEur: budget ? +budget : undefined,
          maxPaybackYears: maxPayback ? +maxPayback : undefined,
          priceSensitivityPct: [-20, 0, 40],
        }),
        manual
          ? api.projects.simulate(detail.project.id, manual.id).then((r) => ({
              name: manual.name,
              npvEur: r.result.finance.npvEur,
              paybackYears: r.result.finance.paybackYears,
              investmentEur: r.result.finance.investmentEur,
              selfSufficiencyPct: r.result.balance.selfSufficiencyRate * 100,
              dcAcRatio: r.result.strings.dcAcRatio,
              annualAcKwh: r.result.energy.annualAcKwh,
            }))
          : Promise.resolve(null),
      ]);
      setSweep(sw);
      setBaseline(base);
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function accept(c: DesignCandidate): Promise<void> {
    setAccepting(c.rank);
    setNote("");
    try {
      const res = await api.projects.autodesign(detail.project.id, { apply: true, applyRank: c.rank });
      await onReload();
      setNote(res.createdDesign ? `Created "${res.createdDesign.name}" as a new AI variant — your manual design is untouched. Open Simulation or Accessories to inspect it.` : "Accepted.");
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setAccepting(null);
    }
  }

  const nf = (v: number): string => Math.round(v).toLocaleString("de-DE");

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 style={{ marginBottom: 4 }}>✨ AI Auto-Designer</h3>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
        Sweeps the entire catalogue — inverter power classes (DC/AC target 1.2), hybrid + battery pairs sized to your consumption, integrated systems like Tesla Powerwall 3, and both dispatch strategies. Every candidate runs the full 8 760-hour simulation and is ranked by NPV. Accepting creates a <b>new</b> design variant; your manual design is never overwritten.
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap", marginBottom: 10 }}>
        <div className="field">
          <label>Objective</label>
          <select value={objective} onChange={(e) => setObjective(e.target.value)}>
            <option value="npv">Maximum lifetime profit (NPV)</option>
            <option value="payback">Fastest payback</option>
            <option value="autarky">Maximum self-sufficiency</option>
            <option value="cost">Lowest investment</option>
            <option value="production">Maximum production</option>
          </select>
        </div>
        <div className="field"><label>Budget cap € (optional)</label><input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 20000" style={{ width: 120 }} /></div>
        <div className="field"><label>Max payback yrs (optional)</label><input type="number" value={maxPayback} onChange={(e) => setMaxPayback(e.target.value)} placeholder="e.g. 6" style={{ width: 110 }} /></div>
        <button className="btn btn-primary" disabled={busy || detail.surfaces.length === 0} onClick={() => void runSweep()}>
          {busy ? <span className="spinner" /> : sweep ? "↻ Re-run AI Engineer" : "▶ Run AI Engineer"}
        </button>
      </div>
      {note && <div className={`msg ${note.startsWith("Error") ? "err" : "ok"}`} style={{ marginTop: 10 }}>{note}</div>}
      {sweep && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 6 }}>
            {sweep.evaluated} configurations simulated · module: {sweep.module.name}{sweep.module.wp ? ` (${sweep.module.wp} Wp)` : ""} · weather: {sweep.weatherSource === "pvgis_sarah3" ? "PVGIS SARAH3" : "synthetic climatology"}
          </div>
          <table className="tbl">
            <thead>
              <tr><th>#</th><th>System</th><th className="r">DC/AC</th><th className="r">Autarky</th><th className="r">Invest</th><th className="r">NPV</th><th className="r">Payback</th><th /></tr>
            </thead>
            <tbody>
              {baseline && (
                <tr style={{ background: "var(--row-highlight)" }}>
                  <td><span className="chip">you</span></td>
                  <td><b>{baseline.name}</b><br /><span className="muted" style={{ fontSize: 11 }}>Your manual design — baseline for comparison</span></td>
                  <td className="r">{baseline.dcAcRatio.toFixed(2)}</td>
                  <td className="r">{baseline.selfSufficiencyPct.toFixed(0)} %</td>
                  <td className="r">{nf(baseline.investmentEur)} €</td>
                  <td className="r"><b>{nf(baseline.npvEur)} €</b></td>
                  <td className="r">{baseline.paybackYears != null ? `${baseline.paybackYears.toFixed(1)} yr` : "—"}</td>
                  <td />
                </tr>
              )}
              {sweep.candidates.map((c) => {
                const beatsBaseline = baseline ? c.npvEur > baseline.npvEur : false;
                return (
                  <tr key={c.rank} style={c.rank === 1 ? { background: "var(--row-success)" } : undefined}>
                    <td>{c.rank}</td>
                    <td>
                      <b>{c.label}</b>
                      {c.rank === 1 && <span className="chip ok" style={{ marginLeft: 6 }}>best</span>}
                      {!c.stringsValid && <span className="chip bad" style={{ marginLeft: 6 }}>strings invalid</span>}
                    {!c.meetsConstraints && <span className="chip warn" style={{ marginLeft: 6 }}>outside constraints</span>}
                      <br /><span className="muted" style={{ fontSize: 11 }}>{c.rationale}</span>
                    </td>
                    <td className="r">{c.dcAcRatio.toFixed(2)}</td>
                    <td className="r">{c.selfSufficiencyPct.toFixed(0)} %</td>
                    <td className="r">{nf(c.investmentEur)} €</td>
                    <td className="r"><b style={beatsBaseline ? { color: "var(--pos)" } : undefined}>{nf(c.npvEur)} €</b></td>
                    <td className="r">{c.paybackYears != null ? `${c.paybackYears.toFixed(1)} yr` : "—"}</td>
                    <td className="r">
                      <button className="btn btn-sm" disabled={accepting !== null || !c.stringsValid} onClick={() => void accept(c)}>
                        {accepting === c.rank ? <span className="spinner" /> : "Accept as AI design"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {sweep.alternatives.length > 1 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3 style={{ marginBottom: 6 }}>Recommended alternatives & trade-offs</h3>
              {sweep.alternatives.map((a) => (
                <div key={a.rank} style={{ marginBottom: 6 }}>
                  <span className={`chip ${a.title === "Empfehlung" ? "ok" : "info"}`}>#{a.rank} {a.title}</span>{" "}
                  <span style={{ fontSize: 12.5 }}>{a.tradeOff}</span>
                </div>
              ))}
            </div>
          )}
          {sweep.sensitivity.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3 style={{ marginBottom: 6 }}>Sensitivity: electricity price (winner, full re-simulation)</h3>
              <table className="tbl">
                <thead><tr><th>Price scenario</th><th className="r">NPV</th><th className="r">Payback</th><th className="r">Benefit yr 1</th></tr></thead>
                <tbody>
                  {sweep.sensitivity.map((sp) => (
                    <tr key={sp.electricityPriceChangePct} style={sp.electricityPriceChangePct === 0 ? { background: "var(--row-neutral)" } : undefined}>
                      <td>{sp.electricityPriceChangePct === 0 ? "today" : `${sp.electricityPriceChangePct > 0 ? "+" : ""}${sp.electricityPriceChangePct} %`}</td>
                      <td className="r"><b>{nf(sp.npvEur)} €</b></td>
                      <td className="r">{sp.paybackYears != null ? `${sp.paybackYears.toFixed(1)} yr` : "—"}</td>
                      <td className="r">{nf(sp.annualBenefitYear1Eur)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Accessories panel ---------------------------- */

const PKG_CATEGORIES: { id: string; label: string; icon: string }[] = [
  { id: "mounting", label: "Mounting system", icon: "🔩" },
  { id: "dc", label: "DC electrical", icon: "⚡" },
  { id: "ac", label: "AC electrical & protection", icon: "🔌" },
  { id: "monitoring", label: "Monitoring & battery", icon: "📡" },
];

function PackagePanel({ detail, onReload, onNext }: { detail: ProjectDetail; onReload: () => Promise<void>; onNext: () => void }): JSX.Element {
  const design = detail.designs[detail.designs.length - 1];
  const [pkg, setPkg] = useState<InstallationPackage | null>(null);
  const [drafts, setDrafts] = useState<Record<string, number>>({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    if (!design) return;
    try {
      setPkg(await api.projects.designPackage(detail.project.id, design.id));
      setDrafts({});
      setErr("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => {
    void load();
  }, [design?.id]);

  function setQty(sku: string, qty: number): void {
    setDrafts((d) => ({ ...d, [sku]: Math.max(0, qty) }));
  }

  async function save(): Promise<void> {
    if (!design) return;
    setBusy(true);
    try {
      const merged: Record<string, number> = { ...((design.accessoryOverrides as Record<string, number> | null) ?? {}), ...drafts };
      await api.projects.updateDesign(detail.project.id, design.id, { accessoryOverrides: merged });
      await onReload();
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function resetAll(): Promise<void> {
    if (!design) return;
    setBusy(true);
    try {
      await api.projects.updateDesign(detail.project.id, design.id, { accessoryOverrides: null });
      await onReload();
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (!design) {
    return (
      <div>
        <h1>Accessories</h1>
        <div className="msg err">Create a design first — the package is derived from your layout and string plan.</div>
      </div>
    );
  }

  const qtyOf = (i: { sku: string; qty: number }) => drafts[i.sku] ?? i.qty;
  const dirty = Object.keys(drafts).length > 0;

  return (
    <div>
      <h1>Accessories & installation package</h1>
      <div className="sub">
        Quantities are derived from your actual layout ({pkg ? `${pkg.moduleCount} modules, ${fmt(pkg.dcKwp, 1)} kWp` : "…"}) — rails from row lengths, clamps from segments, connectors from strings, protection from the inverter. Every line shows its rule and stays editable, AXIS-style.
      </div>
      {err && <div className="msg err">{err}</div>}
      {!pkg ? (
        <span className="spinner" />
      ) : (
        <>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>Main hardware</h3>
            <table className="tbl">
              <tbody>
                {pkg.hardware.map((h, i) => (
                  <tr key={i}>
                    <td><b>{h.name}</b><br /><span className="muted" style={{ fontSize: 11 }}>{h.detail}</span></td>
                    <td className="r">{h.qty} ×</td>
                    <td className="r">{fmt(h.unitPriceEur)} €</td>
                    <td className="r"><b>{eur(h.totalEur)}</b></td>
                  </tr>
                ))}
                <tr><td colSpan={3}><b>Hardware total</b></td><td className="r"><b>{eur(pkg.hardwareTotalEur)}</b></td></tr>
              </tbody>
            </table>
          </div>

          {PKG_CATEGORIES.map((cat) => {
            const items = pkg.accessories.items.filter((i) => i.category === cat.id);
            if (items.length === 0) return null;
            return (
              <div className="card" key={cat.id} style={{ marginBottom: 14 }}>
                <h3 style={{ marginBottom: 8 }}>{cat.icon} {cat.label} <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· {eur(pkg.accessories.byCategory[cat.id] ?? 0)}</span></h3>
                <table className="tbl">
                  <thead><tr><th>Item</th><th className="r" style={{ width: 130 }}>Qty</th><th className="r">Unit €</th><th className="r">Total €</th></tr></thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.sku} style={qtyOf(i) === 0 ? { opacity: 0.45 } : undefined}>
                        <td>
                          <b>{i.name}</b>{i.optional && <span className="chip" style={{ marginLeft: 6 }}>optional</span>}
                          <br /><span className="muted" style={{ fontSize: 11 }}>{i.rule}</span>
                        </td>
                        <td className="r" style={{ whiteSpace: "nowrap" }}>
                          <button className="btn btn-sm" onClick={() => setQty(i.sku, qtyOf(i) - 1)}>−</button>{" "}
                          <input
                            type="number"
                            value={qtyOf(i)}
                            min={0}
                            onChange={(e) => setQty(i.sku, +e.target.value)}
                            style={{ width: 58, textAlign: "right", padding: "4px 6px" }}
                          />{" "}
                          <button className="btn btn-sm" onClick={() => setQty(i.sku, qtyOf(i) + 1)}>＋</button>
                          <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>{i.unit}</span>
                        </td>
                        <td className="r">{fmt(i.unitPriceEur, 2)}</td>
                        <td className="r">{eur(qtyOf(i) * i.unitPriceEur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {pkg.accessories.notes.map((n, i) => (
            <div className="msg info" key={i}>{n}</div>
          ))}

          <div className="tiles">
            <div className="tile"><div className="v">{eur(pkg.hardwareTotalEur)}</div><div className="l">Main hardware</div></div>
            <div className="tile"><div className="v">{eur(pkg.accessories.totalEur)}</div><div className="l">Accessories & small parts</div></div>
            <div className="tile hl"><div className="v">{eur(pkg.grandTotalEur)}</div><div className="l">Complete installation package</div></div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-primary" disabled={busy || !dirty} onClick={() => void save()}>
              {busy ? <span className="spinner" /> : dirty ? `Save ${Object.keys(drafts).length} change(s)` : "Saved"}
            </button>
            <button className="btn" disabled={busy} onClick={() => void resetAll()}>↺ Auto quantities</button>
            <button className="btn" onClick={onNext}>Continue: Simulation →</button>
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
            Accessory prices are wholesale defaults — override any of them in your Price book (key "accessory:&lt;sku&gt;"). The full package flows into the BOM CSV and your wholesaler purchase orders.
          </div>
        </>
      )}
    </div>
  );
}

/* ----------------------------- Results panel ----------------------------- */

function ResultsPanel({ detail, sim, setSim, setError, onNext }: {
  detail: ProjectDetail;
  sim: SimulationResponse | null;
  setSim: (s: SimulationResponse) => void;
  setError: (e: string) => void;
  onNext: () => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [source, setSource] = useState("pvgis_sarah3");
  const [provider, setProvider] = useState("generic");
  const design = detail.designs[detail.designs.length - 1];

  async function run(): Promise<void> {
    if (!design) return;
    setBusy(true);
    setError("");
    try {
      setSim(await api.projects.simulate(detail.project.id, design.id, { weatherSource: source, tariffProvider: provider }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const r = sim?.result;
  return (
    <div>
      <h1>Energy & financial simulation</h1>
      <div className="sub">Hourly simulation: PVGIS satellite irradiation → PV model → load profile → battery dispatch → EEG 2026 financials (incl. Solarspitzengesetz).</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="pvgis_sarah3">PVGIS SARAH3 (satellite, recommended)</option>
          <option value="synthetic_climatology">Synthetic climatology (offline/demo)</option>
        </select>
        <select value={provider} onChange={(e) => setProvider(e.target.value)} title="Dynamic tariff supplier">
          <option value="generic">Dynamic tariff: generic</option>
          <option value="octopus">dynamicOctopus (15-min)</option>
          <option value="tibber">Tibber</option>
          <option value="awattar">aWATTar HOURLY</option>
        </select>
        <button className="btn btn-primary" disabled={busy || !design} onClick={() => void run()}>
          {busy ? <><span className="spinner" /> Simulating…</> : "▶ Run simulation"}
        </button>
        {!design && <span className="muted">Create a design first.</span>}
        {sim && <span className="chip info">{sim.weatherSource}</span>}
      </div>
      {r && (
        <>
          <div className="tiles">
            <div className="tile hl"><div className="v">{fmt(r.energy.annualAcKwh)} kWh</div><div className="l">Annual production</div></div>
            <div className="tile"><div className="v">{fmt(r.energy.specificYieldKwhPerKwp)}</div><div className="l">kWh / kWp</div></div>
            <div className="tile"><div className="v">{fmt(r.energy.performanceRatio * 100, 1)}%</div><div className="l">Performance ratio</div></div>
            <div className="tile"><div className="v">{fmt(r.balance.selfSufficiencyRate * 100)}%</div><div className="l">Self-sufficiency</div></div>
            <div className="tile"><div className="v">{fmt(r.balance.selfConsumedKwh)} kWh</div><div className="l">Self-consumed</div></div>
            <div className="tile"><div className="v">{fmt(r.balance.gridFeedInKwh)} kWh</div><div className="l">Grid feed-in</div></div>
          </div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>Monthly production</h3>
            <BarChart labels={MONTHS} values={r.energy.monthlyAcKwh} unit="kWh" />
          </div>
          <div className="tiles">
            <div className="tile hl"><div className="v">{r.finance.paybackYears ? `${fmt(r.finance.paybackYears, 1)} yr` : "–"}</div><div className="l">Payback</div></div>
            <div className="tile"><div className="v">{eur(r.finance.investmentEur)}</div><div className="l">Investment ({fmt(r.cost.eurPerKwp)} €/kWp{r.cost.vatRate === 0 ? ", 0% VAT" : ""})</div></div>
            <div className="tile"><div className="v">{eur(r.finance.annualBenefitYear1Eur)}</div><div className="l">Benefit year 1</div></div>
            <div className="tile"><div className="v">{eur(r.finance.npvEur)}</div><div className="l">NPV (25 yr)</div></div>
            <div className="tile"><div className="v">{r.finance.irrPct ? `${fmt(r.finance.irrPct, 1)}%` : "–"}</div><div className="l">IRR</div></div>
            <div className="tile"><div className="v">{fmt(r.finance.lcoeEurPerKwh * 100, 1)} ct</div><div className="l">LCOE / kWh</div></div>
          </div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>Cumulative cash flow — 25 years</h3>
            <CashflowChart cashflow={r.finance.cashflow} investment={r.finance.investmentEur} />
          </div>
          {r.dynamicTariff && (
            <div className="card" style={{ marginBottom: 14 }}>
              <h3 style={{ marginBottom: 8 }}>Dynamic tariff analysis — {r.dynamicTariff.provider} (EPEX day-ahead)</h3>
              <div className="tiles">
                <div className="tile"><div className="v">{fmt(r.dynamicTariff.spotAvgCtPerKwh, 1)} ct</div><div className="l">Avg. spot price / kWh</div></div>
                <div className="tile"><div className="v">{fmt(r.dynamicTariff.negativeHourSharePct, 1)}%</div><div className="l">Negative-price hours</div></div>
                <div className="tile"><div className="v">{fmt(r.dynamicTariff.feedInNegativeSharePct, 1)}%</div><div className="l">Feed-in hit by §51 (exact)</div></div>
                <div className="tile"><div className="v">{eur(r.dynamicTariff.importCostDynamicEur)}</div><div className="l">Import cost dynamic /yr (flat: {eur(r.dynamicTariff.importCostFlatEur)})</div></div>
                {r.dynamicTariff.arbitrageGainEur > 0 && (
                  <div className="tile"><div className="v">{eur(r.dynamicTariff.arbitrageGainEur)}</div><div className="l">Battery arbitrage /yr</div></div>
                )}
                <div className={`tile ${r.dynamicTariff.additionalAnnualBenefitEur > 0 ? "hl" : ""}`}>
                  <div className="v">{r.dynamicTariff.additionalAnnualBenefitEur >= 0 ? "+" : ""}{eur(r.dynamicTariff.additionalAnnualBenefitEur)}</div>
                  <div className="l">Extra benefit /yr vs flat tariff</div>
                </div>
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                Feed-in remuneration already excludes negative-price quarter-hours per Solarspitzengesetz — computed exactly from the hourly price series, not a flat haircut. Planning series is EPEX-calibrated; production connects live aWATTar/ENTSO-E data.
              </div>
            </div>
          )}
          {r.inverterGroups && r.inverterGroups.length > 0 && (
            <div className="card" style={{ marginBottom: 14 }}>
              <h3 style={{ marginBottom: 8 }}>Inverter banks</h3>
              <table className="tbl">
                <thead><tr><th>Bank</th><th>Inverter</th><th className="r">DC kWp</th><th className="r">AC kW</th><th className="r">DC/AC</th><th className="r">kWh/yr</th><th>Strings</th></tr></thead>
                <tbody>
                  {r.inverterGroups.map((g, i) => (
                    <tr key={i}>
                      <td><b>{g.label}</b></td>
                      <td>{g.count}× {g.inverterModel}</td>
                      <td className="r">{fmt(g.dcKwp, 1)}</td>
                      <td className="r">{fmt(g.acKw, 1)}</td>
                      <td className="r">{fmt(g.dcAcRatio, 2)}</td>
                      <td className="r">{fmt(g.annualAcKwh)}</td>
                      <td>{g.strings.stringCount} × {g.strings.modulesPerString} {g.strings.valid ? <span className="chip ok">ok</span> : <span className="chip bad">check</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>Electrical design</h3>
            <div style={{ fontSize: 13 }}>
              {r.strings.stringCount} string(s) × {r.strings.modulesPerString} modules · Voc(cold) {fmt(r.strings.vocColdV, 1)} V · DC/AC {fmt(r.strings.dcAcRatio, 2)} {r.strings.valid ? <span className="chip ok">valid</span> : <span className="chip bad">invalid</span>}
            </div>
            {[...r.strings.violations, ...r.strings.warnings].map((m, i) => <div key={i} className="msg err" style={{ marginTop: 8 }}>{m}</div>)}
            {r.issues.filter((i) => i.severity !== "error").map((i, k) => <div key={k} className="msg info" style={{ marginTop: 8 }}>{i.message}</div>)}
          </div>
          <div className="card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>Cost breakdown</h3>
            <table className="tbl">
              <tbody>
                {r.cost.positions.map((p, i) => (
                  <tr key={i}><td>{p.label}</td><td className="r">{eur(p.amountEur)}</td></tr>
                ))}
                <tr><td><b>Total (net{r.cost.vatRate === 0 ? ", 0% VAT §12 UStG" : ""})</b></td><td className="r"><b>{eur(r.cost.totalNetEur)}</b></td></tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <button className="btn btn-o btn-sm" onClick={() => void downloadFile(`/projects/${detail.project.id}/designs/${design!.id}/bom.csv`, "BOM.csv")}>⬇ BOM CSV</button>
            <button className="btn btn-o btn-sm" onClick={() => void downloadFile(`/projects/${detail.project.id}/designs/${design!.id}/cashflow.csv`, "Cashflow.csv")}>⬇ Cash-flow CSV</button>
          </div>
          <div className="msg info">{sim!.attribution}</div>
          <div className="msg info">{sim!.disclaimer}</div>
          <button className="btn btn-primary" onClick={onNext}>Continue: Regulation →</button>
        </>
      )}
    </div>
  );
}

/* ----------------------------- Permits panel ----------------------------- */

/* ---------------------------- Procurement panel --------------------------- */

const ORDER_NEXT: Record<string, { to: string; label: string }[]> = {
  DRAFT: [{ to: "ORDERED", label: "Mark ordered" }, { to: "CANCELLED", label: "Cancel" }],
  ORDERED: [{ to: "DELIVERED", label: "Mark delivered" }, { to: "PARTIALLY_DELIVERED", label: "Partial delivery" }, { to: "CANCELLED", label: "Cancel" }],
  PARTIALLY_DELIVERED: [{ to: "DELIVERED", label: "Mark delivered" }],
  DELIVERED: [],
  CANCELLED: [],
};

function ProcurementPanel({ detail, onNext }: { detail: ProjectDetail; onNext: () => void }): JSX.Element {
  const design = detail.designs[detail.designs.length - 1];
  const [orders, setOrders] = useState<ProcurementOrder[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState("");
  const [newSupplier, setNewSupplier] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState<string | null>(null);

  async function load(): Promise<void> {
    const [o, sp] = await Promise.all([api.projects.procurement(detail.project.id), api.projects.suppliers()]);
    setOrders(o);
    setSuppliers(sp);
  }
  useEffect(() => { void load(); }, [detail.project.id]);

  async function addSupplier(): Promise<void> {
    if (!newSupplier.trim()) return;
    const sp = await api.projects.createSupplier({ name: newSupplier.trim() });
    setNewSupplier("");
    setSuppliers((x) => [...x, sp]);
    setSupplierId(sp.id);
  }

  async function createOrder(): Promise<void> {
    if (!design) return;
    setBusy(true);
    setNote("");
    try {
      await api.projects.createOrder(detail.project.id, { designId: design.id, supplierId: supplierId || undefined });
      await load();
      setNote("Purchase order created from the complete installation package (hardware + all accessories).");
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function transition(orderId: string, to: string): Promise<void> {
    await api.projects.updateOrder(detail.project.id, orderId, { status: to });
    await load();
  }

  const chipClass = (st: string): string =>
    st === "DELIVERED" ? "chip ok" : st === "CANCELLED" ? "chip bad" : st === "DRAFT" ? "chip" : "chip info";

  return (
    <div>
      <h1>Procurement</h1>
      <div className="sub">One click turns the design's installation package into a purchase order for your wholesaler — every module, inverter, battery, rail, clamp and connector with net prices from your price book.</div>
      {note && <div className={`msg ${note.startsWith("Error") ? "err" : "ok"}`}>{note}</div>}
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginBottom: 8 }}>New order</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={{ minWidth: 180 }}>
            <option value="">— supplier (optional) —</option>
            {suppliers.map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
          </select>
          <input placeholder="add supplier…" value={newSupplier} onChange={(e) => setNewSupplier(e.target.value)} style={{ width: 160 }} />
          <button className="btn btn-sm" onClick={() => void addSupplier()}>+ Add</button>
          <button className="btn btn-primary" disabled={busy || !design} onClick={() => void createOrder()}>
            {busy ? <span className="spinner" /> : "Create order from package"}
          </button>
        </div>
      </div>
      {orders.map((o) => (
        <div className="card" key={o.id} style={{ marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div>
              <b>Order {o.id.slice(0, 8)}</b> <span className={chipClass(o.status)}>{o.status}</span>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {o.items.length} positions · {eur(o.totalNetEur ?? 0)} net · {suppliers.find((sp) => sp.id === o.supplierId)?.name ?? "no supplier"}
                {o.orderedAt ? ` · ordered ${new Date(o.orderedAt).toLocaleDateString("de-DE")}` : ""}
                {o.deliveredAt ? ` · delivered ${new Date(o.deliveredAt).toLocaleDateString("de-DE")}` : ""}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <button className="btn btn-o btn-sm" onClick={() => setOpen(open === o.id ? null : o.id)}>{open === o.id ? "Hide" : "Positions"}</button>
              <button className="btn btn-o btn-sm" onClick={() => void downloadFile(`/projects/${detail.project.id}/procurement/${o.id}/po.csv`, `Bestellung-${o.id.slice(0, 8)}.csv`)}>⬇ PO CSV</button>
              {ORDER_NEXT[o.status]?.map((t) => (
                <button key={t.to} className="btn btn-sm" onClick={() => void transition(o.id, t.to)}>{t.label}</button>
              ))}
            </div>
          </div>
          {open === o.id && (
            <table className="tbl" style={{ marginTop: 10 }}>
              <thead><tr><th>Item</th><th className="r">Qty</th><th className="r">Unit €</th><th className="r">Total €</th></tr></thead>
              <tbody>
                {o.items.map((it, i) => (
                  <tr key={i}>
                    <td>{it.label}<br /><span className="muted" style={{ fontSize: 10.5 }}>{it.componentType}</span></td>
                    <td className="r">{it.qty} {it.unit}</td>
                    <td className="r">{fmt(it.unitEur, 2)}</td>
                    <td className="r">{eur(it.qty * it.unitEur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
      {orders.length === 0 && <div className="muted" style={{ fontSize: 12.5 }}>No orders yet.</div>}
      <div style={{ marginTop: 12 }}>
        <button className="btn" onClick={onNext}>Continue: Installation →</button>
      </div>
    </div>
  );
}

/* ---------------------------- Installation panel -------------------------- */

const TASK_STATUS: { id: string; label: string }[] = [
  { id: "OPEN", label: "Open" },
  { id: "IN_PROGRESS", label: "In progress" },
  { id: "DONE", label: "Done" },
  { id: "BLOCKED", label: "Blocked" },
];

function InstallationPanel({ detail }: { detail: ProjectDetail }): JSX.Element {
  const [view, setView] = useState<InstallationView | null>(null);
  const [crew, setCrew] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [comm, setComm] = useState({ electricianName: "", electricianRegistrationNo: "", meterNumber: "", mastrNumber: "" });
  const [note, setNote] = useState("");

  async function load(): Promise<void> {
    const v = await api.projects.installation(detail.project.id);
    setView(v);
    if (v.plan) {
      setCrew(v.plan.crewName ?? "");
      setStart(v.plan.scheduledStart?.slice(0, 10) ?? "");
      setEnd(v.plan.scheduledEnd?.slice(0, 10) ?? "");
    }
    if (v.commissioning) {
      setComm({
        electricianName: v.commissioning.electricianName ?? "",
        electricianRegistrationNo: v.commissioning.electricianRegistrationNo ?? "",
        meterNumber: v.commissioning.meterNumber ?? "",
        mastrNumber: v.commissioning.mastrNumber ?? "",
      });
    }
  }
  useEffect(() => { void load(); }, [detail.project.id]);

  async function savePlan(): Promise<void> {
    setBusy(true);
    try {
      await api.projects.upsertInstallationPlan(detail.project.id, {
        crewName: crew || null,
        scheduledStart: start || null,
        scheduledEnd: end || null,
      });
      await load();
      setNote("Installation plan saved — task board generated from the design (battery-aware).");
    } catch (e) {
      setNote(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function setTask(taskId: string, status: string): Promise<void> {
    await api.projects.updateInstallationTask(detail.project.id, taskId, { status });
    await load();
  }

  async function saveCommissioning(markDone: boolean): Promise<void> {
    setBusy(true);
    try {
      await api.projects.saveCommissioning(detail.project.id, {
        ...comm,
        ...(markDone ? { commissionedAt: new Date().toISOString() } : {}),
      });
      await load();
      setNote(markDone ? "Commissioning recorded — remember the MaStR registration deadline (1 month)." : "Commissioning data saved.");
    } finally {
      setBusy(false);
    }
  }

  const doneCount = view?.tasks.filter((t) => t.status === "DONE").length ?? 0;

  return (
    <div>
      <h1>Installation</h1>
      <div className="sub">Schedule the crew, work the task board through to commissioning, and record the E.8 protocol data.</div>
      {note && <div className={`msg ${note.startsWith("Error") ? "err" : "ok"}`}>{note}</div>}
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginBottom: 8 }}>Schedule</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <div className="field"><label>Crew</label><input value={crew} onChange={(e) => setCrew(e.target.value)} placeholder="Team Nord" style={{ width: 150 }} /></div>
          <div className="field"><label>Start</label><input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
          <div className="field"><label>End</label><input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          <button className="btn btn-primary" disabled={busy} onClick={() => void savePlan()}>
            {busy ? <span className="spinner" /> : view?.plan ? "Update plan" : "Create plan + task board"}
          </button>
        </div>
      </div>
      {view?.plan && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h3 style={{ marginBottom: 8 }}>Task board <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>· {doneCount}/{view.tasks.length} done</span></h3>
          <table className="tbl">
            <tbody>
              {view.tasks.map((t) => (
                <tr key={t.id} style={t.status === "DONE" ? { opacity: 0.55 } : undefined}>
                  <td>
                    <b>{t.title}</b>
                    <br /><span className="muted" style={{ fontSize: 10.5 }}>{t.category}{t.assignee ? ` · ${t.assignee}` : ""}{t.doneAt ? ` · ${new Date(t.doneAt).toLocaleDateString("de-DE")}` : ""}</span>
                  </td>
                  <td className="r" style={{ width: 150 }}>
                    <select value={t.status} onChange={(e) => void setTask(t.id, e.target.value)}>
                      {TASK_STATUS.map((st) => <option key={st.id} value={st.id}>{st.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card">
        <h3 style={{ marginBottom: 8 }}>Commissioning (E.8) {view?.commissioning?.commissionedAt && <span className="chip ok">commissioned {new Date(view.commissioning.commissionedAt).toLocaleDateString("de-DE")}</span>}</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div className="field"><label>Electrician</label><input value={comm.electricianName} onChange={(e) => setComm({ ...comm, electricianName: e.target.value })} placeholder="Elektro Huber GmbH" /></div>
          <div className="field"><label>Registration no.</label><input value={comm.electricianRegistrationNo} onChange={(e) => setComm({ ...comm, electricianRegistrationNo: e.target.value })} placeholder="HWK-…" /></div>
          <div className="field"><label>Meter number</label><input value={comm.meterNumber} onChange={(e) => setComm({ ...comm, meterNumber: e.target.value })} /></div>
          <div className="field"><label>MaStR number</label><input value={comm.mastrNumber} onChange={(e) => setComm({ ...comm, mastrNumber: e.target.value })} placeholder="SEE…" /></div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button className="btn" disabled={busy} onClick={() => void saveCommissioning(false)}>Save</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void saveCommissioning(true)}>Mark commissioned now</button>
        </div>
      </div>
    </div>
  );
}

function PermitsPanel({ detail, onReload, onNext }: { detail: ProjectDetail; onReload: () => Promise<void>; onNext: () => void }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const KINDS: Record<string, string> = {
    DSO_GRID_APPLICATION: "Netzanmeldung (grid application)",
    MASTR_REGISTRATION: "Marktstammdatenregister",
    SMART_METER_ORDER: "Smart meter (iMSys) + Steuerbox",
    STRUCTURAL_CHECK: "Structural / roof load check",
    BUILDING_PERMIT: "Building permit (B-Plan)",
    DIRECT_MARKETING_CONTRACT: "Direktvermarktung contract",
    EEG_TENDER: "BNetzA tender (≥1 MWp)",
    COMPLETION_NOTICE: "Completion notice (Fertigmeldung)",
    METER_CHANGE: "Meter change",
  };

  async function generate(): Promise<void> {
    setBusy(true);
    setNote("");
    try {
      await api.projects.generatePermits(detail.project.id);
      await onReload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1>German regulation & process assistant</h1>
      <div className="sub">Checklist generated from your simulated system size, project type and Bundesland. Preparation support — final approval rests with DSO, authority and certified installer.</div>
      {detail.permitTasks.length === 0 ? (
        <>
          {note && <div className="msg err">{note}</div>}
          <button className="btn btn-primary" disabled={busy} onClick={() => void generate()}>
            {busy ? <span className="spinner" /> : "⚙ Generate checklist from simulation"}
          </button>
        </>
      ) : (
        <>
          {detail.permitTasks.map((t) => (
            <div key={t.id} className={`check ${t.status === "DONE" || t.status === "APPROVED" ? "donec" : ""}`}>
              <div className="cb" onClick={() => void api.projects.updatePermit(detail.project.id, t.id, t.status === "DONE" ? "OPEN" : "DONE").then(onReload)}>
                {t.status === "DONE" || t.status === "APPROVED" ? "✓" : ""}
              </div>
              <div className="t"><b>{KINDS[t.kind] ?? t.kind}</b><span>{t.notes}</span></div>
            </div>
          ))}
          {detail.designs.length > 0 && (
            <div className="card" style={{ marginTop: 12 }}>
              <h3 style={{ marginBottom: 6 }}>📑 Netzanmeldung package</h3>
              <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
                3-page PDF from your current design: DSO data sheet (VDE-AR-N 4105) with MaStR checklist, single-line diagram with sized cables and protection (LS/SPD/gPV), and the string plan + cable schedule with voltage-drop calculation.
              </div>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const d = detail.designs[detail.designs.length - 1]!;
                  void downloadFile(`/projects/${detail.project.id}/designs/${d.id}/permit-package.pdf`, "Netzanmeldung.pdf");
                }}
              >
                ⬇ Download permit package (PDF)
              </button>
            </div>
          )}
          <button className="btn btn-primary" onClick={onNext} style={{ marginTop: 12 }}>Continue: Proposals →</button>
        </>
      )}
    </div>
  );
}

/* ---------------------------- Proposals panel ---------------------------- */

function ProposalsPanel({ detail, sim, onReload }: { detail: ProjectDetail; sim: SimulationResponse | null; onReload: () => Promise<void> }): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [signUrl, setSignUrl] = useState("");
  const design = detail.designs[detail.designs.length - 1];

  async function create(): Promise<void> {
    if (!design) return;
    setBusy(true);
    setNote("");
    try {
      await api.projects.createProposal(detail.project.id, design.id);
      await onReload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function send(proposalId: string): Promise<void> {
    setNote("");
    try {
      const res = await api.projects.sendProposal(detail.project.id, proposalId);
      setSignUrl(`${window.location.origin}/v1/public/sign${res.signUrl.replace("/sign", "")}`);
      await onReload();
    } catch (e) {
      setNote(e instanceof Error ? e.message : String(e));
    }
  }

  const chip = (s: string) => (s === "ACCEPTED" ? "ok" : s === "SENT" ? "info" : s === "REJECTED" || s === "EXPIRED" ? "bad" : "");

  return (
    <div>
      <h1>Proposals</h1>
      <div className="sub">Branded PDF with charts and signature block; send issues a public e-signature link — customer acceptance flips the project to ACCEPTED automatically.</div>
      {note && <div className="msg err">{note}</div>}
      <button className="btn btn-primary" disabled={busy || !design || !sim} onClick={() => void create()} style={{ marginBottom: 14 }}>
        {busy ? <span className="spinner" /> : "＋ Create proposal from latest simulation"}
      </button>
      {!sim && <span className="muted" style={{ marginLeft: 10 }}>Run a simulation first.</span>}
      {signUrl && (
        <div className="msg ok">
          Signing link (send to your customer): <code style={{ fontSize: 11 }}>{signUrl}</code>{" "}
          <button className="btn btn-sm" onClick={() => void navigator.clipboard.writeText(signUrl)}>⧉ Copy</button>
        </div>
      )}
      {detail.proposals.length > 0 && (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>Number</th><th>Status</th><th className="r">Net</th><th className="r">Gross</th><th>Valid until</th><th></th></tr></thead>
            <tbody>
              {detail.proposals.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.number}</b></td>
                  <td>
                    <span className={`chip ${chip(p.status)}`}>{p.status}</span>
                    {p.signedAt && <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>✍ {new Date(p.signedAt).toLocaleDateString("de-DE")}</span>}
                  </td>
                  <td className="r">{eur(p.totalNetEur)}</td>
                  <td className="r">{eur(p.totalGrossEur)}</td>
                  <td>{p.validUntil ? new Date(p.validUntil).toLocaleDateString("de-DE") : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn btn-sm btn-o" onClick={() => void downloadFile(`/projects/${detail.project.id}/proposals/${p.id}/pdf`, `Angebot-${p.number}.pdf`)}>⬇ PDF</button>{" "}
                    {p.status === "DRAFT" && <button className="btn btn-sm" onClick={() => void send(p.id)}>✉ Send for signature</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
